/* KraftCover — bundle-aware cart page interactions
 *
 * The full /cart page renders bundle parent rows with a custom +/- and
 * remove widget (no anchor links, no input[name=updates[]]) so Focal's
 * standard form mechanisms don't touch them. This script intercepts those
 * widgets and fans the action out to all sibling lines (parent + children)
 * via a single /cart/update.js call, mirroring cart-drawer.js behavior.
 *
 * Sibling keys are read from `data-bundle-keys` on the <tr> (server-rendered
 * from the cart attribute `_kc_bundle_<uid>` written by kc-bundle.js at
 * add-to-cart time).
 */
(function () {
  if (window.__kcCartPageBundleBound) return;
  window.__kcCartPageBundleBound = true;

  const ENDPOINT = '/cart/update.js';
  const HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let busy = false;

  function setRowLoading(row, state) {
    row.classList.toggle('is-loading', state);
    row.querySelectorAll('button[data-bundle-action]').forEach((b) => { b.disabled = state; });
  }

  /* Toast: reuses the cart drawer's `.tb-toast` styling (drawer CSS is loaded
     on every page including /cart). Native alert() is jarring in a premium
     cart — toast keeps brand voice. role=alert when assertive so SR users
     get the failure announcement. */
  function showToast(msg, opts) {
    const assertive = !!(opts && opts.assertive);
    document.querySelector('.tb-toast')?.remove();
    const t = document.createElement('div');
    t.className = 'tb-toast';
    t.setAttribute('role', assertive ? 'alert' : 'status');
    t.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => {
      t.style.opacity = '0';
      t.style.transition = 'opacity .3s';
      setTimeout(() => t.remove(), 300);
    }, 2800);
  }

  function siblingKeys(row) {
    const csv = (row.getAttribute('data-bundle-keys') || '').trim();
    if (!csv) return [];
    return csv.split(',').map((s) => s.trim()).filter(Boolean);
  }

  function displayedQty(row) {
    const el = row.querySelector('.line-item__bundle-qty-display');
    const n = parseInt((el && el.textContent) || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  /* Fetch authoritative qty from server: any of the bundle's source-line
     keys returns the same qty (Cart Transform fans them out 1:1). Avoids
     stale-DOM races where rapid clicks compute next from outdated display. */
  async function authoritativeQty(keys, fallback) {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!r.ok) return fallback;
      const cart = await r.json();
      const k = keys[0];
      const line = cart.items && cart.items.find((i) => i.key === k);
      if (line && Number.isFinite(line.quantity) && line.quantity > 0) return line.quantity;
    } catch (_) { /* fall through */ }
    return fallback;
  }

  async function fanout(keys, qty, uid) {
    const updates = {};
    keys.forEach((k) => { updates[k] = qty; });
    const payload = { updates };
    // qty 0 = bundle gone: clear its cart attributes (keys + variants summary)
    if (qty === 0 && uid) payload.attributes = { [`_kc_bundle_${uid}`]: '', [`_kc_bundle_${uid}_variants`]: '' };
    const r = await fetch(ENDPOINT, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Sepet güncellenemedi');
    }
    return r.json();
  }

  /* Soft-reload via Section Rendering API: replaces ONLY the cart section's
     wrapper instead of full window reload. Wins ~700ms + preserves scroll +
     no full-page repaint (drawer↔/cart parity). Falls back to hard reload
     on any unexpected response shape — never leaves the page in a half-state. */
  async function softReload() {
    try {
      const r = await fetch('/cart?section_id=main-cart', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!r.ok) throw new Error('section ' + r.status);
      const html = await r.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      // Shopify wraps section render output in <div id="shopify-section-{id}">.
      // Bail to hard reload if we got anything else.
      const newSection = tmp.firstElementChild;
      if (!newSection || !newSection.id || newSection.id.indexOf('shopify-section-') !== 0) {
        throw new Error('unexpected wrapper');
      }
      const oldSection = document.getElementById(newSection.id);
      if (!oldSection) throw new Error('missing target ' + newSection.id);
      oldSection.replaceWith(newSection);
      // Document-delegated click handler survives the swap.
      busy = false;
      return true;
    } catch (e) {
      console.warn('[kc-cart-page] soft reload failed, falling back to full reload', e);
      window.location.reload();
      return false;
    }
  }

  async function handleClick(e) {
    const btn = e.target.closest('button[data-bundle-action]');
    if (!btn) return;
    const row = btn.closest('tr.line-item--bundle');
    if (!row) return;

    e.preventDefault();
    e.stopPropagation();
    if (busy) return;

    const keys = siblingKeys(row);
    if (!keys.length) {
      // Fallback: legacy bundle without uid/keys — confident, action-first.
      showToast('Bu paket eski formatta. Önce sil, sonra yeniden ekle.', { assertive: true });
      return;
    }

    const action = btn.getAttribute('data-bundle-action');
    busy = true;
    setRowLoading(row, true);

    // Optimistic display update: bump the qty number immediately so the user
    // sees instant feedback even before the fanout round-trip completes.
    // Snapshot the prior display so we can roll back on error.
    const display = row.querySelector('.line-item__bundle-qty-display');
    const priorText = display ? display.textContent : null;

    try {
      // Read authoritative qty from /cart.js — DOM display can be stale on
      // rapid clicks since the page only refreshes on full reload.
      const cur = await authoritativeQty(keys, displayedQty(row));
      let nextQty;
      if (action === 'increase') nextQty = cur + 1;
      else if (action === 'decrease') nextQty = Math.max(0, cur - 1);
      else if (action === 'remove') nextQty = 0;
      else { setRowLoading(row, false); busy = false; return; }

      // Apply optimistic display for inc/dec. Remove keeps the row visible
      // (loading state) until soft reload paints the post-remove state.
      if (display && action !== 'remove') {
        display.textContent = String(nextQty);
      }

      await fanout(keys, nextQty, row.getAttribute('data-bundle-uid'));
      // Section-render the cart so Focal's table, totals, shipping bar, and
      // Cart Transform all re-evaluate consistently — without losing scroll.
      await softReload();
    } catch (err) {
      console.error('[kc-cart-page]', err);
      // Roll back the optimistic display.
      if (display && priorText !== null) display.textContent = priorText;
      showToast(err.message || 'Sepet güncellenemedi. Tekrar dene.', { assertive: true });
      setRowLoading(row, false);
      busy = false;
    }
  }

  function bind() {
    document.addEventListener('click', handleClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
